import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { isEqual, pick } from 'lodash';

import { OrderDirection } from '@constants';
import { LocalStorageService } from '@services';

export type ListSort = { key: string; direction: OrderDirection };

export interface IListViewBase<G extends string, C extends string> {
  query: string;
  groupBy: G;
  columns: C[];
  sort: ListSort;
}

export interface IListViewConfig<
  F extends object,
  G extends string,
  C extends string,
> {
  emptyFilters: F;
  readFilters: (params: URLSearchParams) => F;
  writeFilters: (
    filters: F,
    set: (key: string, value: string | null) => void,
  ) => void;
  countFilters: (filters: F) => number;
  groupOptions: G[];
  defaultGroup: G;
  groupStorageKey: string;
  defaultColumns: C[];
  isColumn: (id: string) => id is C;
  defaultSort: ListSort;
}

export const splitParam = (value: string | null): string[] =>
  value ? value.split(',').filter(Boolean) : [];

const isSameSort = (a: ListSort, b: ListSort): boolean =>
  a.key === b.key && a.direction === b.direction;

const isDefaultColumns = <C extends string>(
  columns: C[],
  defaultColumns: C[],
): boolean =>
  columns.length === defaultColumns.length &&
  columns.every((id, index) => id === defaultColumns[index]);

export const useListViewState = <
  F extends object,
  G extends string,
  C extends string,
>(
  config: IListViewConfig<F, G, C>,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: entryState } = useLocation();

  const readGroupBy = useCallback((): G => {
    const stored = LocalStorageService.getState<string>(config.groupStorageKey);

    return config.groupOptions.includes(stored as G)
      ? (stored as G)
      : config.defaultGroup;
  }, [config]);

  const state: IListViewBase<G, C> & F = useMemo(() => {
    const rawSort = searchParams.get('sort');
    const [sortKey, sortDir] = rawSort ? rawSort.split(':') : [];
    const rawColumns = searchParams.get('cols');
    const rawGroup = searchParams.get('group');

    return {
      query: searchParams.get('q') ?? '',
      ...config.readFilters(searchParams),
      groupBy: config.groupOptions.includes(rawGroup as G)
        ? (rawGroup as G)
        : readGroupBy(),
      columns: rawColumns
        ? rawColumns.split(',').filter(config.isColumn)
        : config.defaultColumns,
      sort:
        sortKey &&
        (sortDir === OrderDirection.ASC || sortDir === OrderDirection.DESC)
          ? { key: sortKey, direction: sortDir }
          : config.defaultSort,
    };
  }, [searchParams, config, readGroupBy]);

  const write = useCallback(
    (patch: Partial<IListViewBase<G, C> & F>) => {
      const next = { ...state, ...patch };

      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);

          const set = (key: string, value: string | null) =>
            value ? params.set(key, value) : params.delete(key);

          set('q', next.query);
          config.writeFilters(next, set);
          set('group', next.groupBy === readGroupBy() ? null : next.groupBy);
          set(
            'cols',
            isDefaultColumns(next.columns, config.defaultColumns)
              ? null
              : next.columns.join(','),
          );
          set(
            'sort',
            isSameSort(next.sort, config.defaultSort)
              ? null
              : `${next.sort.key}:${next.sort.direction}`,
          );

          return params;
        },
        { replace: true, state: entryState },
      );

      if (patch.groupBy !== undefined) {
        LocalStorageService.setState(config.groupStorageKey, patch.groupBy);
      }
    },
    [state, config, readGroupBy, setSearchParams, entryState],
  );

  const toggleSort = useCallback(
    (key: string) => {
      if (state.sort.key !== key) {
        write({ sort: { key, direction: OrderDirection.ASC } } as Partial<
          IListViewBase<G, C> & F
        >);
        return;
      }

      write({
        sort:
          state.sort.direction === OrderDirection.ASC
            ? { key, direction: OrderDirection.DESC }
            : config.defaultSort,
      } as Partial<IListViewBase<G, C> & F>);
    },
    [state.sort, write, config],
  );

  const reset = useCallback(
    () =>
      write({
        query: '',
        ...config.emptyFilters,
        columns: config.defaultColumns,
        sort: config.defaultSort,
      } as Partial<IListViewBase<G, C> & F>),
    [write, config],
  );

  const activeFilterCount = config.countFilters(state);

  const isDefault =
    !state.query &&
    isEqual(
      pick(state, Object.keys(config.emptyFilters)),
      config.emptyFilters,
    ) &&
    isSameSort(state.sort, config.defaultSort) &&
    isDefaultColumns(state.columns, config.defaultColumns);

  return { state, write, toggleSort, reset, activeFilterCount, isDefault };
};
